import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, Section, StatCard, Spinner, RiskBadge } from "@/components/ui-parts";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Download } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function BoardSummary() {
  const { withScope, activeTenantId, currentTenant } = useTenant();
  const [stats, setStats] = useState(null);
  const [coverage, setCoverage] = useState([]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats", { params: withScope() }),
      api.get("/dashboard/framework-coverage", { params: withScope() }),
    ]).then(([a, b]) => { setStats(a.data); setCoverage(b.data); });
    // eslint-disable-next-line
  }, [activeTenantId]);

  const downloadPptx = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      const scope = withScope();
      if (scope.tenant_id) params.set("tenant_id", scope.tenant_id);
      const res = await fetch(`${BASE}/api/dashboard/board-deck/pptx${params.toString() ? "?" + params : ""}`, {
        credentials: "include",
        headers: localStorage.getItem("gacrf_token") ? { Authorization: `Bearer ${localStorage.getItem("gacrf_token")}` } : {},
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `GACRF_board_deck_${new Date().toISOString().slice(0, 10)}.pptx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
    finally { setDownloading(false); }
  };

  if (!stats) return <div className="p-8"><Spinner /></div>;

  return (
    <div className="p-8 max-w-[1600px]" data-testid="board-page">
      <PageHeader eyebrow="Overview" title="Board Dashboard"
        description={`Executive-level view of ${currentTenant?.name || "the organization"}'s cyber resilience posture, framework coverage, and prioritized risk focus.`}
        actions={
          <button className="btn btn-primary btn-sm" onClick={downloadPptx} disabled={downloading} data-testid="export-pptx-btn">
            <Download size={12} /> {downloading ? "Exporting..." : "Export Board Deck (PPTX)"}
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard tone="brand" label="ECRI" value={stats.ecri} subValue={`${stats.certification} track`} />
        <StatCard label="Maturity" value={stats.avg_score.toFixed(2)} subValue={stats.maturity} />
        <StatCard label="Risk Posture" value={stats.risk_posture} tone={stats.risk_posture === "Critical" ? "critical" : stats.risk_posture === "High" ? "warning" : "default"} />
        <StatCard label="Coverage" value={`${Math.round((stats.assessed / (stats.total || 1)) * 100)}%`} subValue={`${stats.assessed}/${stats.total}`} />
      </div>

      <Section title="Executive Cyber Resilience Summary">
        <div className="p-5 space-y-3 text-sm leading-relaxed">
          <p className="text-slate-300">
            <strong className="text-slate-100">Executive Cyber Resilience Index (ECRI):</strong>{" "}
            <span className="font-mono text-[#00E5FF] text-lg">{stats.ecri}/100</span>. Overall maturity assessed as <strong className="text-slate-100">{stats.maturity}</strong> with <strong className="text-slate-100">{stats.certification}</strong> certification readiness.
          </p>
          <p className="text-slate-400">
            {stats.assessed} of {stats.total} requirements have been evaluated across {stats.domains.length} governance and capability domains.
            The current risk posture is assessed as <strong className={stats.risk_posture === "Critical" ? "text-red-400" : stats.risk_posture === "High" ? "text-orange-400" : "text-slate-200"}>{stats.risk_posture}</strong>,
            with {stats.high_risk_count} high/critical-risk requirements requiring executive attention.
          </p>
          <p className="text-slate-400">
            <strong className="text-slate-100">Primary focus:</strong> Reduce exposure by closing evidence gaps and elevating capability maturity in the weakest three domains. Prioritize remediation of Critical-risk requirements with score below 3.
          </p>
        </div>
      </Section>

      <Section title="Domain Maturity Overview">
        <div className="p-5" style={{ height: 320, minHeight: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.domains}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="domain" tick={{ fill: "#94A3B8", fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={80} />
              <YAxis domain={[0, 5]} tick={{ fill: "#94A3B8", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", fontSize: 12 }} />
              <Bar dataKey="score" fill="#00E5FF" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Framework Coverage">
          <table className="data-table">
            <thead><tr><th>Framework</th><th>Mapped</th><th>Avg</th><th>Status</th></tr></thead>
            <tbody>
              {coverage.map((r) => (
                <tr key={r.framework}>
                  <td className="text-slate-200">{r.framework}</td>
                  <td className="mono">{r.mapped}</td>
                  <td className="mono">{r.avg_score.toFixed(2)}</td>
                  <td>
                    <span className={`badge ${r.status === "Strong" ? "badge-success" : r.status === "Moderate" ? "badge-medium" : "badge-critical"}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section title="Top 5 Priority Gaps">
          <table className="data-table">
            <thead><tr><th>Req ID</th><th>Domain</th><th>Risk</th><th>Score</th></tr></thead>
            <tbody>
              {stats.top_gaps.slice(0, 5).map((r) => (
                <tr key={r.requirementId}>
                  <td className="mono">{r.requirementId}</td>
                  <td>{r.domain}</td>
                  <td><RiskBadge level={r.riskLevel} /></td>
                  <td className="mono">{r.score.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>
    </div>
  );
}
